import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  FormHelperText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { ROUTES } from "../../routes/routerConfig";
import { InputField, InputFieldOTP } from "../../components/ui/Inputs";
import { PrimaryButton, OutlinedPrimaryButton } from "../../components/ui/Button";
import ButtonLoader from "../../components/ui/ButtonLoader";
import SupportCategoriesGrid from "../../components/common/SupportCategoriesGrid";
import PreLoginHero from "../../components/common/PreLoginHero";
import DigitalChannels from "../../components/common/DigitalChannels";
import { preLoginFontSx } from "../../components/common/preLoginFont";
import service from "../../services/api/service";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import {
  setQrcToken,
  setQrcFlowType,
  setQrcLan,
  setQrcLanOptions,
  setQrcAppIds,
  setQrcEmail,
  setQrcName,
  setQrcUpgradedToBorrower,
  clearAuth,
  clearQrcToken,
  qrcVerifiedSelector,
} from "../../services/redux/slices/authSlice";
import {
  setloanAccountNumberOptions,
  setSelectedloanAccountNumber,
  setSelectedloanData,
} from "../../services/redux/slices/optionSlice";
import DialogStatusPopup from "../../components/common/DialogStatusPopup";
import { emailRegex } from "../../utils/helpers";
import { encrypt, decryptObject, encryptObject } from "../../utils/crypto";
import { ALL_TICKET_STATUS_CODES } from "../support/support.types";
// Shared with the post-login screens, which read the same list from the same endpoint.
import {
  extractAppIds,
  extractLanList,
  parseUserVerificationRecords,
} from "../support/qrcIdentifiers";

const TEAL = "#00B3B5";
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 180;
// ALL_TICKET_STATUS_CODES (support.types) is used here to check whether a non-borrower
// already has any ticket, regardless of its status.

type VerifyStep = "idle" | "mobile" | "otp" | "success";
type FlowChoice = "question" | "borrower" | "nonBorrower";
// Which tool the visitor came for. Both intents run the exact same question + verification
// box — the only difference is where a successful verification lands them.
type ServiceIntent = "raise" | "tickets";

// shortLabel is used on xs, where the two tabs sit side by side as underline tabs and the full
// labels would each wrap onto a second line at 390px.
const INTENT_TABS: { key: ServiceIntent; label: string; shortLabel: string }[] = [
  { key: "raise", label: "Raise a service request here", shortLabel: "Raise Request" },
  { key: "tickets", label: "View existing tickets", shortLabel: "View Tickets" },
];

const formatCooldown = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const qrcVerified = useSelector(qrcVerifiedSelector);

  const [flowChoice, setFlowChoice] = useState<FlowChoice>("question");
  const [serviceIntent, setServiceIntent] = useState<ServiceIntent>("raise");

  const [verifyStep, setVerifyStep] = useState<VerifyStep>("idle");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpMeta, setOtpMeta] = useState<{ key: string; createdAt: string } | null>(null);
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpInputRefs = useRef<HTMLInputElement[]>([]);
  const verifySectionRef = useRef<HTMLDivElement>(null);

  // ── Non-borrower verification (name + mobile + email, dual OTP) ─────────────
  const [nbFullName, setNbFullName] = useState("");
  const [nbMobile, setNbMobile] = useState("");
  const [nbEmail, setNbEmail] = useState("");
  const [nbErrors, setNbErrors] = useState<{ fullName?: string; mobile?: string; email?: string }>({});
  const [nbSendingOtp, setNbSendingOtp] = useState(false);
  const [nbOtpSent, setNbOtpSent] = useState(false);
  const [nbMobileOtp, setNbMobileOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
  const [nbEmailOtp, setNbEmailOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
  const [nbMobileOtpMeta, setNbMobileOtpMeta] = useState<{ key: string; createdAt: string } | null>(null);
  const [nbEmailOtpMeta, setNbEmailOtpMeta] = useState<{ key: string; createdAt: string } | null>(null);
  const [nbMobileCooldown, setNbMobileCooldown] = useState(0);
  const [nbEmailCooldown, setNbEmailCooldown] = useState(0);
  const [nbVerifyingMobile, setNbVerifyingMobile] = useState(false);
  const [nbVerifyingEmail, setNbVerifyingEmail] = useState(false);
  const [nbMobileVerified, setNbMobileVerified] = useState(false);
  const [nbEmailVerified, setNbEmailVerified] = useState(false);
  const nbMobileOtpRefs = useRef<HTMLInputElement[]>([]);
  const nbEmailOtpRefs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (verifyStep !== "otp" || cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [verifyStep, cooldown]);

  useEffect(() => {
    if (nbMobileCooldown <= 0 && nbEmailCooldown <= 0) return;
    const timer = setTimeout(() => {
      setNbMobileCooldown((s) => Math.max(0, s - 1));
      setNbEmailCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [nbMobileCooldown, nbEmailCooldown]);

  const scrollToVerify = () => {
    verifySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const startVerifyFlow = () => {
    setVerifyStep("mobile");
    scrollToVerify();
  };

  const handleSendOtp = async () => {
    const trimmedValue = mobileNumber.trim();
    const isMobile = /^\d{10}$/.test(trimmedValue);
    const isEmail = emailRegex.test(trimmedValue);
    if (!isMobile && !isEmail) {
      setMobileError("Enter a valid 10-digit mobile number or email ID");
      return;
    }
    setMobileError("");
    setSendingOtp(true);
    try {
      const resp = await service.support.sendOtp(
        isMobile ? { phone: encrypt(trimmedValue) } : { email: encrypt(trimmedValue) }
      );
      const otpData = resp?.data?.status === "success" ? resp.data?.data : undefined;
      setOtpMeta({
        key: otpData?.value ?? "",
        createdAt: otpData?.otpTime ?? new Date().toISOString(),
      });
    } catch (error: any) {
      // Still show the API error, but let the user reach the OTP screen anyway.
      setOtpMeta({ key: "", createdAt: new Date().toISOString() });
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    } finally {
      // Always show the OTP box, even if send-OTP failed.
      setOtp(new Array(OTP_LENGTH).fill(""));
      setVerifyStep("otp");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setSendingOtp(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 1);
    const next = [...otp];
    if (numericValue || value === "") {
      next[index] = numericValue;
      setOtp(next);
      if (numericValue && index < OTP_LENGTH - 1) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Called once OTP verification succeeds (either the borrower single-mobile-OTP path,
  // or the non-borrower path once both mobile + email are verified). Exchanges the
  // qrc/otp/verify result for a state-checkpoint token that unlocks Raise Request /
  // View Tickets / Ticket Details for this pre-login session (see QrcRoutes).
  // Whether this visitor already has at least one ticket, in any status. Keyed the same way
  // ViewTickets lists them: on the verified email for a non-borrower, on the LAN(s) from
  // user-verification for a borrower.
  // Runs after the QRC token is set, so the request is authenticated (see interceptor).
  const hasExistingTickets = async (
    scope: { email: string } | { lan: string[] }
  ): Promise<boolean> => {
    const isEmailScope = "email" in scope;
    if (isEmailScope ? !scope.email : !scope.lan.length) return false;
    try {
      const encryptedData = encryptObject({ ...scope, status: ALL_TICKET_STATUS_CODES });
      const res = await service.support.getTicketListing({ data: encryptedData }, 1, 1);
      const payload = res?.data;
      const rawData = payload?.data;
      const encryptedTickets = typeof rawData === "string" ? rawData : rawData?.data;
      const tickets = decryptObject(encryptedTickets);
      return Array.isArray(tickets) && tickets.length > 0;
    } catch {
      return false;
    }
  };

  // `borrowerLans` carries the LAN list from the caller's own qrc/user-verification look-up, so
  // this never calls that endpoint a second time. Both borrower entry points supply it:
  // handleVerifyOtp ("Yes, I do") and handleNonBorrowerBothVerified (the "No, I don't" →
  // existing-customer upgrade, which also sets upgradedFromNonBorrower).
  // Lands the verified visitor on View Tickets or Raise a Request. Split out of
  // completeVerification so handleNonBorrowerBothVerified can establish the QRC session first,
  // run its user-verification look-up with that session, and only then route.
  const navigateAfterVerification = async (
    effectiveFlow: "borrower" | "nonBorrower",
    email: string,
    lanList: string[]
  ) => {
    // Only switched to the success view here, once the visitor is actually moving on — a
    // failed user-verification look-up must leave them on the OTP screen.
    setVerifyStep("success");
    if (serviceIntent === "tickets") {
      // Came in through the "View existing tickets" tab — go straight to the listing,
      // for borrowers and non-borrowers alike.
      navigate(ROUTES.ContactUs.viewTickets.locationPath);
      return;
    }
    // Same rule for both flows: a visitor who already has a ticket lands on the listing,
    // everyone else on Raise a Request.
    const hasTickets = await hasExistingTickets(
      effectiveFlow === "nonBorrower" ? { email } : { lan: lanList }
    );
    navigate(
      hasTickets
        ? ROUTES.ContactUs.viewTickets.locationPath
        : ROUTES.ContactUs.raiseRequest.locationPath
    );
  };

  // Returns whether the QRC session was established, plus the QRC token (state checkpoint's
  // correlationIndex) for callers that send a request with it right away.
  // `deferNavigation` leaves routing to the caller.
  const completeVerification = async (
    rawVerifyData?: { id?: number; name?: string; phoneNumber?: string; email?: string } | string,
    borrowerLans?: { lanList: string[]; appIds?: string[]; upgradedFromNonBorrower?: boolean },
    options?: { deferNavigation?: boolean }
  ): Promise<{ success: boolean; qrcToken: string | null }> => {
    const failed = { success: false, qrcToken: null };
    // qrc/otp/verify's "data" field comes back as an encrypted blob when payload
    // encryption is on (and as a plain object when it's off) — decryptObject handles both.
    const verifyData: { id?: number; name?: string; phoneNumber?: string; email?: string } | undefined =
      typeof rawVerifyData === "string" ? decryptObject(rawVerifyData) : rawVerifyData;
    if (!verifyData?.id) {
      dispatch(setStatusPopup({ status: "error", message: "Verification failed. Please try again." }));
      return failed;
    }
    // How this session behaves from here on — not necessarily the screen they came in through.
    const effectiveFlow: "borrower" | "nonBorrower" =
      borrowerLans?.upgradedFromNonBorrower || flowChoice !== "nonBorrower" ? "borrower" : "nonBorrower";
    try {
      const checkpointResp = await service.support.stateCheckpoint(verifyData.id, {
        identifier: encrypt(verifyData.phoneNumber || verifyData.email || ""),
      });
      if (checkpointResp?.data?.status?.toLowerCase() === "success") {
        // A QRC-verified visitor is a pre-login identity, and it can be a different person
        // than whoever is logged in on this tab. Drop that login session so the Contact Us
        // pages render the pre-login chrome (no sidebar, no "Welcome back <user>" header)
        // and every request from here on carries the QRC token, not the login one.
        dispatch(clearAuth());
        // The option slice is persisted to sessionStorage and survives clearAuth, so the loan
        // accounts of whoever was logged in on this tab used to leak into the QRC dropdowns —
        // a verified visitor saw LANs their verification never returned.
        dispatch(setloanAccountNumberOptions(null));
        dispatch(setSelectedloanAccountNumber(null));
        dispatch(setSelectedloanData(null));
        dispatch(setQrcToken(checkpointResp.data?.correlationIndex ?? null));
        dispatch(setQrcFlowType(effectiveFlow));
        // Flags the "No, I don't" → existing-customer upgrade so the hub can explain why the
        // visitor landed on the borrower tools (see ContactUsHub).
        dispatch(setQrcUpgradedToBorrower(!!borrowerLans?.upgradedFromNonBorrower));
        // Verified customer/entity name from qrc/otp/verify — shown on the Contact Us hub.
        dispatch(setQrcName(verifyData.name || null));

        // Non-borrower flow: persist the verified email — View Tickets sends this
        // instead of a LAN when listing tickets for a non-borrower (see ViewTickets.tsx).
        if (effectiveFlow === "nonBorrower") {
          dispatch(setQrcEmail(verifyData.email || nbEmail.trim() || null));
        }

        // Borrower flow: the caller's user-verification identifier(s) — see extractLanList, which
        // yields a LAN per loan and a CRM id per application still awaiting one. The first
        // pre-fills the Raise Request "Loan Account/Application Number" input, and every one
        // found (across all matched customer records) feeds that dropdown and the Lan/Application
        // ID dropdown on the Contact Us hub. This used to re-fetch user-verification here, gated
        // on the identifier being a 10-digit mobile — so verifying with a registered *email* left
        // the LAN list empty and the Raise Request dropdown had nothing to show.
        if (borrowerLans) {
          dispatch(setQrcLan(borrowerLans.lanList[0] ?? null));
          dispatch(setQrcLanOptions(borrowerLans.lanList));
          // Sent as applicationId by the ticket listing; see ViewTickets.
          dispatch(setQrcAppIds(borrowerLans.appIds ?? []));
        }

        if (!options?.deferNavigation) {
          await navigateAfterVerification(
            effectiveFlow,
            verifyData.email || nbEmail.trim(),
            borrowerLans?.lanList ?? []
          );
        }
        return { success: true, qrcToken: checkpointResp.data?.correlationIndex ?? null };
      } else {
        dispatch(
          setStatusPopup({
            status: "error",
            message: checkpointResp?.data?.message,
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
    return failed;
  };

  const handleVerifyOtp = async () => {
  setVerifyingOtp(true);
  
  try {
    // 1. First API call: Verify OTP
    const resp = await service.support.verifyOtp({
      inputCode: encrypt(otp.join("")),
      identifier: encrypt(mobileNumber),
    });

    if (resp?.data?.status?.toLowerCase() === "success") {

      // 2. Establish the QRC session first (state checkpoint → QRC token) so the
      // user-verification look-up below is sent with it. Routing waits for that look-up.
      const session = await completeVerification(resp.data?.data, undefined, {
        deferNavigation: true,
      });
      if (!session.success) return;

      // 3. Check User Verification BEFORE navigating, authorized with the QRC token from the
      // state checkpoint (sent as "Bearer <token>" via authOverride).
      const verificationResp = await service.support.userVerification(
        { identifier: encrypt(mobileNumber.trim()) },
        session.qrcToken ?? undefined
      );

      // 4. If it fails, show error and STOP
      if (verificationResp?.data?.status?.toLowerCase() !== "success") {
        dispatch(
          setStatusPopup({
            status: "error",
            message: verificationResp?.data?.message,
          })
        );
        return; // This stops the function from going to the next page
      }

      // 5. Both succeeded — store the LANs and navigate. The LAN list comes from the look-up
      // above, so it is populated whether the visitor verified with a mobile number or a
      // registered email.
      const verificationRecords = parseUserVerificationRecords(verificationResp?.data?.data);
      const lanList = extractLanList(verificationRecords);
      dispatch(setQrcLan(lanList[0] ?? null));
      dispatch(setQrcLanOptions(lanList));
      dispatch(setQrcAppIds(extractAppIds(verificationRecords)));
      // Reached only when user-verification succeeded (a failure returned above).
      await navigateAfterVerification("borrower", "", lanList);
    } else {
      // First API (verifyOtp) failed
      dispatch(setStatusPopup({ status: "error", message: resp?.data?.message }));
    }
  } catch (error: any) {
    // Catch any network or server errors
    dispatch(
      setStatusPopup({
        status: "error",
        message: error?.response?.data?.message || "An error occurred",
      })
    );
  } finally {
    setVerifyingOtp(false);
  }
};

  const isOtpComplete = otp.every((digit) => digit !== "");
  const cooldownLabel = formatCooldown(cooldown);

  // Borrower flow counterpart to handleNbChangeInfo — drops back to the mobile/email input
  // (which un-disables it) so the visitor can correct what they typed before re-sending an OTP.
  const handleChangeInfo = () => {
    setVerifyStep("mobile");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpMeta(null);
    setCooldown(0);
    setMobileError("");
  };

  // ── Non-borrower handlers ────────────────────────────────────────────────

  const validateNbForm = (): boolean => {
    const errors: typeof nbErrors = {};
    if (!nbFullName.trim()) errors.fullName = "Full name is required";
    if (!/^\d{10}$/.test(nbMobile)) errors.mobile = "Enter a valid 10-digit mobile number";
    if (!emailRegex.test(nbEmail.trim())) errors.email = "Enter a valid email address";
    setNbErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNbSendOtp = async () => {
    if (!validateNbForm()) return;
    setNbSendingOtp(true);
    const payload = {
      name: nbFullName.trim(),
      phone: encrypt(nbMobile),
      email: encrypt(nbEmail.trim()),
    };
    try {
      const resp = await service.support.sendOtp(payload);
      if (resp?.data?.status === "success") {
        // Single OTP covers both channels for now — both boxes share the same meta.
        const meta = {
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
        };
        setNbMobileOtpMeta(meta);
        setNbEmailOtpMeta(meta);
        setNbMobileOtp(new Array(OTP_LENGTH).fill(""));
        setNbEmailOtp(new Array(OTP_LENGTH).fill(""));
        setNbOtpSent(true);
        setNbMobileCooldown(RESEND_COOLDOWN_SECONDS);
        setNbEmailCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        // Send-OTP failed — stay on the form and show the API error (no OTP screen).
        dispatch(setStatusPopup({ status: "error", message: resp?.data?.message }));
      }
    } catch (error: any) {
      // Send-OTP failed — stay on the form and show the API error (no OTP screen).
      dispatch(setStatusPopup({ status: "error", message: error?.response?.data?.message }));
    } finally {
      setNbSendingOtp(false);
    }
  };

  const handleNbResend = async () => {
    const payload = { name: nbFullName.trim(), phone: encrypt(nbMobile), email: encrypt(nbEmail.trim()) };
    try {
      const resp = await service.support.sendOtp(payload);
      if (resp?.data?.status === "success") {
        const meta = {
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
        };
        setNbMobileOtpMeta(meta);
        setNbEmailOtpMeta(meta);
        setNbMobileOtp(new Array(OTP_LENGTH).fill(""));
        setNbEmailOtp(new Array(OTP_LENGTH).fill(""));
        setNbMobileCooldown(RESEND_COOLDOWN_SECONDS);
        setNbEmailCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  };

  const handleNbResendMobile = handleNbResend;
  const handleNbResendEmail = handleNbResend;

  const handleNbOtpChange = (
    otpArr: string[],
    setOtpArr: (v: string[]) => void,
    refs: React.MutableRefObject<HTMLInputElement[]>,
    index: number,
    value: string
  ) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 1);
    const next = [...otpArr];
    if (numericValue || value === "") {
      next[index] = numericValue;
      setOtpArr(next);
      if (numericValue && index < OTP_LENGTH - 1) {
        refs.current[index + 1]?.focus();
      }
    }
  };

  const handleNbOtpKeyDown = (
    otpArr: string[],
    refs: React.MutableRefObject<HTMLInputElement[]>,
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otpArr[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const isNbMobileOtpComplete = nbMobileOtp.every((digit) => digit !== "");
  const isNbEmailOtpComplete = nbEmailOtp.every((digit) => digit !== "");

  // Called once both the non-borrower's mobile + email OTPs are verified. Cross-checks the
  // mobile number against qrc/user-verification: if it already belongs to a customer, this
  // session runs as a borrower (LAN dropdown on Raise a Request, LAN-keyed ticket listing)
  // no matter which screen they came in through — they are not sent back to re-verify.
  const handleNonBorrowerBothVerified = async (
    verifyData?: { id?: number; phoneNumber?: string; email?: string } | string
  ) => {
    // Establish the QRC session first (state checkpoint → QRC token), so the
    // user-verification look-up below is sent with it. Routing waits until we know
    // which flow this visitor ends up in.
    const session = await completeVerification(verifyData, undefined, {
      deferNavigation: true,
    });
    if (!session.success) return;

    const decodedVerifyData: { email?: string } | undefined =
      typeof verifyData === "string" ? decryptObject(verifyData) : verifyData;
    const verifiedEmail = decodedVerifyData?.email || nbEmail.trim();

    try {
      const checkResp = await service.support.userVerification({ identifier: encrypt(nbMobile) });
      const checkRecords = parseUserVerificationRecords(checkResp?.data?.data);
      const lanList = extractLanList(checkRecords);
      // A LAN or a CRM id means this is an existing customer, so run the session as a borrower.
      // A match with neither has nothing to raise the request against, so those users stay on
      // the non-borrower (Concern) form.
      if (lanList.length > 0) {
        // Same borrower state completeVerification sets for an upgraded visitor.
        dispatch(setQrcFlowType("borrower"));
        dispatch(setQrcUpgradedToBorrower(true));
        dispatch(setQrcLan(lanList[0] ?? null));
        dispatch(setQrcLanOptions(lanList));
        dispatch(setQrcAppIds(extractAppIds(checkRecords)));
        await navigateAfterVerification("borrower", verifiedEmail, lanList);
        return;
      }
    } catch {
      // If the look-up itself fails, don't block the user — carry on as a non-borrower.
    }
    await navigateAfterVerification("nonBorrower", verifiedEmail, []);
  };

  const handleNbVerifyMobile = async () => {
    setNbVerifyingMobile(true);
    try {
      const resp = await service.support.verifyOtp({
        inputCode: encrypt(nbMobileOtp.join("")),
        identifier: encrypt(nbMobile),
      });
      if (resp?.data?.status?.toLowerCase() === "success") {
        setNbMobileVerified(true);
        if (nbEmailVerified) await handleNonBorrowerBothVerified(resp.data?.data);
      } else {
        dispatch(setStatusPopup({ status: "error", message: resp?.data?.message }));
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    } finally {
      setNbVerifyingMobile(false);
    }
  };

  const handleNbVerifyEmail = async () => {
    setNbVerifyingEmail(true);
    try {
      const resp = await service.support.verifyOtp({
        inputCode: encrypt(nbEmailOtp.join("")),
        identifier: encrypt(nbEmail.trim()),
      });
      if (resp?.data?.status?.toLowerCase() === "success") {
        setNbEmailVerified(true);
        if (nbMobileVerified) await handleNonBorrowerBothVerified(resp.data?.data);
      } else {
        dispatch(setStatusPopup({ status: "error", message: resp?.data?.message }));
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    } finally {
      setNbVerifyingEmail(false);
    }
  };

  const handleNbChangeInfo = () => {
    setNbOtpSent(false);
    setNbMobileOtp(new Array(OTP_LENGTH).fill(""));
    setNbEmailOtp(new Array(OTP_LENGTH).fill(""));
    setNbMobileVerified(false);
    setNbEmailVerified(false);
  };

  const handleBackToQuestion = () => {
    setFlowChoice("question");

    setVerifyStep("idle");
    setMobileNumber("");
    setMobileError("");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpMeta(null);
    setCooldown(0);

    setNbFullName("");
    setNbMobile("");
    setNbEmail("");
    setNbErrors({});
    setNbOtpSent(false);
    setNbMobileOtp(new Array(OTP_LENGTH).fill(""));
    setNbEmailOtp(new Array(OTP_LENGTH).fill(""));
    setNbMobileOtpMeta(null);
    setNbEmailOtpMeta(null);
    setNbMobileCooldown(0);
    setNbEmailCooldown(0);
    setNbMobileVerified(false);
    setNbEmailVerified(false);
  };

  // Pre-login sessions have no login to end — dropping the QRC token is the logout. Staying on
  // this page and resetting to the question stage is the natural landing spot.
  const handleLogout = () => {
    dispatch(clearQrcToken());
    handleBackToQuestion();
  };

  const isQuestionStage = flowChoice === "question" && verifyStep === "idle";

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", ...preLoginFontSx }}>
      {/* Full-bleed hero; the card below overlaps its lower portion. The headline switches once
          the visitor is past the "do you have a loan with us?" question. */}
      <PreLoginHero
        variant={isQuestionStage ? "greeting" : "request"}
        // Only once a QRC session exists — before that there is nothing to log out of.
        onLogout={qrcVerified ? handleLogout : undefined}
      />
      <Box sx={{ pb: 6 }}>
        {/* Full-width with the design's narrow gutters — the layout is not capped at 1200 */}
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 3, sm: 4 } }}>
          {/* The negative margin lives here, not on the card, so the card and the rail rise
              onto the hero together instead of the rail sitting 92px lower. position/zIndex are
              required: the hero is position:relative, so it paints over static siblings — without
              this the rail's top disappears behind the hero band. */}
          <Box sx={{ position: "relative", zIndex: 1, mt: { xs: "-34px", sm: "-92px" } }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={isQuestionStage ? 12 : 8}>

          {/* Question / verify card. The question stage carries the "Raise a service request here"
              legend pill; the verify steps carry the BACK pill. Mobile drops the teal border for a
              plain raised card (per design). */}
          <Box
            ref={verifySectionRef}
            sx={{
              position: "relative",
              minHeight: isQuestionStage ? "auto" : { md: 470 },
              bgcolor: "#fff",
              borderRadius: "12px",
              border: { xs: "1px solid #EFF1F3", sm: `1.5px solid ${TEAL}` },
              boxShadow: { xs: "0px 4px 18px 0px rgba(75, 70, 92, 0.08)", sm: "none" },
              // Verify steps sit tighter to the card edge than the question card, with extra
              // top room so the BACK pill on the border doesn't crowd the title.
              px: { xs: 2.5, sm: isQuestionStage ? 2.5 : 2 },
              pt: { xs: 2.5, sm: isQuestionStage ? 2.5 : 3.5 },
              pb: { xs: 2.5, sm: 2.5 },
            }}
          >
            {/* Intent toggle, sitting on the card's top border. Both tabs show the same question
                and verification box — the selection only decides where verification lands the
                visitor (Raise a Request vs View Tickets). On xs it flows above the content
                instead, since the border pill has no room there. */}
            {isQuestionStage && (
              <Box
                sx={{
                  position: { xs: "static", sm: "absolute" },
                  top: { sm: 0 },
                  left: { sm: 20 },
                  transform: { sm: "translateY(-50%)" },
                  display: { xs: "flex", sm: "inline-flex" },
                  flexDirection: "row",
                  alignItems: "stretch",
                  width: { xs: "100%", sm: "auto" },
                  gap: { xs: 0, sm: 0.5 },
                  p: { xs: 0, sm: 0.5 },
                  mb: { xs: 2.5, sm: 0 },
                  // sm+ sits on the card's top border, so it needs the opaque white fill and teal
                  // outline to mask it. xs is a flat underline tab strip instead: no track, just
                  // the hair rule that the active tab's bar sits on.
                  bgcolor: { xs: "transparent", sm: "#fff" },
                  border: { xs: "none", sm: `1.5px solid ${TEAL}` },
                  borderBottom: { xs: "1px solid #E5E7EB", sm: `1.5px solid ${TEAL}` },
                  borderRadius: { xs: 0, sm: "999px" },
                }}
              >
                {INTENT_TABS.map((tab) => {
                  const isActive = serviceIntent === tab.key;
                  return (
                    <Button
                      key={tab.key}
                      disableRipple
                      onClick={() => setServiceIntent(tab.key)}
                      sx={{
                        // xs: equal halves of the strip. sm: pills sized to their labels.
                        flex: { xs: 1, sm: "0 0 auto" },
                        borderRadius: { xs: 0, sm: "999px" },
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 1.25, sm: 0.3 },
                        minWidth: 0,
                        lineHeight: 1.45,
                        fontSize: { xs: "13px", sm: "11px" },
                        fontWeight: 700,
                        letterSpacing: { xs: 0, sm: "0.06em" },
                        textTransform: { xs: "none", sm: "uppercase" },
                        whiteSpace: "nowrap",
                        // xs selects with a teal bar under the label — no fill, no shadow. The
                        // -1px pulls the 3px bar over the strip's hair rule so they read as one
                        // line. sm keeps the teal fill that moves between the two pills.
                        bgcolor: { xs: "transparent", sm: isActive ? TEAL : "transparent" },
                        color: isActive ? { xs: TEAL, sm: "#fff" } : "#6b7280",
                        boxShadow: { xs: "none", sm: isActive ? "0px 2px 8px rgba(0,179,181,0.35)" : "none" },
                        borderBottom: {
                          xs: `3px solid ${isActive ? TEAL : "transparent"}`,
                          sm: "none",
                        },
                        mb: { xs: "-1px", sm: 0 },
                        transform: { xs: "none", sm: isActive ? "scale(1)" : "scale(0.96)" },
                        transition: [
                          "background-color 0.28s ease",
                          "color 0.28s ease",
                          "box-shadow 0.28s ease",
                          "border-color 0.28s ease",
                          // Slight overshoot so the selected segment "pops" in.
                          "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
                        ].join(", "),
                        "&:hover": {
                          bgcolor: {
                            xs: isActive ? "transparent" : "rgba(0,179,181,0.06)",
                            sm: isActive ? TEAL : "rgba(0,179,181,0.10)",
                          },
                          color: isActive ? { xs: TEAL, sm: "#fff" } : "#1a1a1a",
                        },
                      }}
                    >
                      {isXsOrSm ? tab.shortLabel : tab.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* BACK pill, sitting on the card's top border like the legend pill */}
            {flowChoice !== "question" && verifyStep !== "success" && (
              <Button
                onClick={handleBackToQuestion}
                startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 20,
                  transform: "translateY(-50%)",
                  bgcolor: "#fff",
                  border: `1.5px solid ${TEAL}`,
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.3,
                  minWidth: 0,
                  color: "#1a1a1a",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  "& .MuiButton-startIcon": { mr: 0.5, ml: 0, color: TEAL },
                  "&:hover": { bgcolor: "#fff", borderColor: "#009799" },
                }}
              >
                Back
              </Button>
            )}

            {/* The key remounts this block whenever the intent tab (or the stage) changes, which
                replays the fade-in — without it the toggle click leaves the box looking untouched.
                Safe to remount: every field here is controlled from component state, not the DOM. */}
            <Box
              key={isQuestionStage ? `question-${serviceIntent}` : "flow"}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: isQuestionStage ? "row" : "column" },
                alignItems: { xs: "flex-start", sm: isQuestionStage ? "center" : "flex-start" },
                justifyContent: "space-between",
                gap: 2,
                "@keyframes intentSwap": {
                  from: { opacity: 0, transform: "translateY(6px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: "intentSwap 0.32s ease both",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                {isQuestionStage && (
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "rgba(0,179,181,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PersonOutlineOutlinedIcon sx={{ color: TEAL, fontSize: 20 }} />
                  </Box>
                )}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize:
                        verifyStep === "success" ? { xs: "18px", sm: "20px" } : { xs: "15px", sm: "16px" },
                      color: "#1a1a1a",
                    }}
                  >
                    {verifyStep === "success"
                      ? "You're verified!"
                      : flowChoice === "question"
                      ? "Do you have an existing loan or application with us?"
                      : flowChoice === "nonBorrower"
                      ? "Verify yourself as a non-borrower"
                      : "Verify yourself to raise request or view tickets"}
                  </Typography>
                  <Typography
                    sx={{ color: "#6b7280", fontSize: verifyStep === "success" ? "14px" : "12px", mt: 0.5 }}
                  >
                    {verifyStep === "success"
                      ? "You can now continue to raise a request or check your existing tickets."
                      : flowChoice === "question"
                      ? "This helps us show you the right tools based on your relationship."
                      : flowChoice === "nonBorrower"
                      ? "Please enter your details to verify and raise a service request instantly"
                      : "Please enter your registered details to access service ticket tools instantly"}
                  </Typography>
                </Box>
              </Box>

              {isQuestionStage && (
                <Box sx={{ display: "flex", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
                  <PrimaryButton
                    fullWidth={isXsOrSm}
                    height="38px"
                    fontSize="13px"
                    startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      setFlowChoice("borrower");
                      startVerifyFlow();
                    }}
                    sx={{
                      px: { xs: 1.5, sm: 3 },
                      whiteSpace: "nowrap",
                      // Equal halves on xs. Without a zero basis these shrink in proportion to
                      // their own label widths, which squeezed "No, I don't" until it truncated.
                      flex: { xs: "1 1 0", sm: "0 0 auto" },
                    }}
                  >
                    Yes, I do
                  </PrimaryButton>
                  <OutlinedPrimaryButton
                    variant="outlined"
                    fullWidth={isXsOrSm}
                    height="38px"
                    borderRadius="6px"
                    // Teal border, dark label — as in the design, not an all-teal button.
                    textColor="#1F2937"
                    startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                    onClick={() => {
                      setFlowChoice("nonBorrower");
                      scrollToVerify();
                    }}
                    sx={{
                      px: { xs: 1.5, sm: 3 },
                      whiteSpace: "nowrap",
                      flex: { xs: "1 1 0", sm: "0 0 auto" },
                    }}
                  >
                    No, I don&apos;t
                  </OutlinedPrimaryButton>
                </Box>
              )}

              {flowChoice === "borrower" && (verifyStep === "mobile" || verifyStep === "otp") && (
                <Box sx={{ width: "100%", maxWidth: 360, mt: 1 }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 0.5 }}>
                    Registered Mobile/ Email Id<Box component="span" sx={{ color: "error.main" }}> *</Box>
                  </Typography>
                  <InputField
                    fullWidth
                    placeholder="Mobile/ Email"
                    value={mobileNumber}
                    disabled={verifyStep === "otp"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setMobileNumber(e.target.value);
                      setMobileError("");
                    }}
                    error={!!mobileError}
                  />
                  {mobileError && <FormHelperText error>{mobileError}</FormHelperText>}

                  {verifyStep === "mobile" && (
                    <PrimaryButton
                      height="38px"
                      fontSize="13px"
                      disabled={sendingOtp}
                      onClick={handleSendOtp}
                      sx={{ mt: 2, px: 3 }}
                    >
                      <ButtonLoader title="Send OTP" isLoading={sendingOtp} />
                    </PrimaryButton>
                  )}

                  {verifyStep === "otp" && (
                    <Box sx={{ mt: 2.5 }}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 1 }}>
                        Enter OTP<Box component="span" sx={{ color: "error.main" }}> *</Box>
                      </Typography>
                      <Grid container spacing={1}>
                        {otp.map((digit, index) => (
                          <Grid item key={index} xs={2}>
                            <InputFieldOTP
                              variant="outlined"
                              type="tel"
                              inputMode="decimal"
                              autoComplete="one-time-code"
                              width="100%"
                              autoFocus={index === 0}
                              value={digit}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleOtpKeyDown(e, index)}
                              inputProps={{ maxLength: 1 }}
                              inputRef={(el: HTMLInputElement) => (otpInputRefs.current[index] = el)}
                            />
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                        <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>{cooldownLabel}</Typography>
                        <Button
                          disabled={cooldown > 0 || sendingOtp}
                          onClick={handleSendOtp}
                          sx={{ textTransform: "none", fontWeight: 700, color: TEAL, p: 0, minWidth: 0 }}
                        >
                          Resend OTP
                        </Button>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
                        {/* Disabled styling comes from PrimaryButton (#E6E6E6 / #B3B3B3) */}
                        <PrimaryButton
                          height="38px"
                          fontSize="13px"
                          disabled={!isOtpComplete || verifyingOtp}
                          onClick={handleVerifyOtp}
                          sx={{ px: 3 }}
                        >
                          <ButtonLoader title="Submit" isLoading={verifyingOtp} />
                        </PrimaryButton>
                        <OutlinedPrimaryButton
                          variant="outlined"
                          height="38px"
                          borderRadius="6px"
                          disabled={verifyingOtp}
                          onClick={handleChangeInfo}
                          sx={{ px: 3 }}
                        >
                          Change Info
                        </OutlinedPrimaryButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {flowChoice === "nonBorrower" && verifyStep !== "success" && (
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 0.5 }}>
                        Full Name<Box component="span" sx={{ color: "error.main" }}> *</Box>
                      </Typography>
                      <InputField
                        fullWidth
                        placeholder="Full Name"
                        value={nbFullName}
                        disabled={nbOtpSent}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setNbFullName(e.target.value);
                          setNbErrors((prev) => ({ ...prev, fullName: undefined }));
                        }}
                        error={!!nbErrors.fullName}
                      />
                      {nbErrors.fullName && <FormHelperText error>{nbErrors.fullName}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 0.5 }}>
                        Mobile Number<Box component="span" sx={{ color: "error.main" }}> *</Box>
                      </Typography>
                      <InputField
                        fullWidth
                        placeholder="9876543210"
                        value={nbMobile}
                        disabled={nbOtpSent}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setNbMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                          setNbErrors((prev) => ({ ...prev, mobile: undefined }));
                        }}
                        error={!!nbErrors.mobile}
                        inputProps={{ maxLength: 10 }}
                      />
                      {nbErrors.mobile && <FormHelperText error>{nbErrors.mobile}</FormHelperText>}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 0.5 }}>
                        Email<Box component="span" sx={{ color: "error.main" }}> *</Box>
                      </Typography>
                      <InputField
                        fullWidth
                        placeholder="you@example.com"
                        value={nbEmail}
                        disabled={nbOtpSent}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setNbEmail(e.target.value);
                          setNbErrors((prev) => ({ ...prev, email: undefined }));
                        }}
                        error={!!nbErrors.email}
                      />
                      {nbErrors.email && <FormHelperText error>{nbErrors.email}</FormHelperText>}
                    </Grid>
                  </Grid>

                  {!nbOtpSent && (
                    <PrimaryButton
                      fullWidth={isXsOrSm}
                      height="38px"
                      fontSize="13px"
                      disabled={nbSendingOtp}
                      onClick={handleNbSendOtp}
                      sx={{ mt: 2.5, px: 4 }}
                    >
                      <ButtonLoader title="Send OTP" isLoading={nbSendingOtp} />
                    </PrimaryButton>
                  )}

                  {nbOtpSent && (
                    <>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ bgcolor: "rgba(0,179,181,0.06)", borderRadius: 2, p: 2 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 1 }}>
                              Enter Mobile OTP<Box component="span" sx={{ color: "error.main" }}> *</Box>
                              {nbMobileVerified && (
                                <Box component="span" sx={{ color: "#10B981", fontWeight: 700, ml: 1 }}>✓ Verified</Box>
                              )}
                            </Typography>
                            <Grid container spacing={1}>
                              {nbMobileOtp.map((digit, index) => (
                                <Grid item key={index} xs={2}>
                                  <InputFieldOTP
                                    variant="outlined"
                                    type="tel"
                                    inputMode="decimal"
                                    autoComplete="one-time-code"
                                    width="100%"
                                    value={digit}
                                    disabled={nbMobileVerified}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      handleNbOtpChange(nbMobileOtp, setNbMobileOtp, nbMobileOtpRefs, index, e.target.value)
                                    }
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                      handleNbOtpKeyDown(nbMobileOtp, nbMobileOtpRefs, e, index)
                                    }
                                    inputProps={{ maxLength: 1 }}
                                    inputRef={(el: HTMLInputElement) => (nbMobileOtpRefs.current[index] = el)}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {!nbMobileVerified && (
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                                  {formatCooldown(nbMobileCooldown)}
                                </Typography>
                                <Button
                                  disabled={nbMobileCooldown > 0}
                                  onClick={handleNbResendMobile}
                                  sx={{ textTransform: "none", fontWeight: 700, color: TEAL, p: 0, minWidth: 0 }}
                                >
                                  Resend OTP
                                </Button>
                              </Box>
                            )}
                            {!nbMobileVerified && (
                              <PrimaryButton
                                fullWidth
                                height="36px"
                                fontSize="13px"
                                disabled={!isNbMobileOtpComplete || nbVerifyingMobile}
                                onClick={handleNbVerifyMobile}
                                sx={{ mt: 1.5 }}
                              >
                                <ButtonLoader title="Verify Mobile OTP" isLoading={nbVerifyingMobile} />
                              </PrimaryButton>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box sx={{ bgcolor: "rgba(0,179,181,0.06)", borderRadius: 2, p: 2 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#6b7280", mb: 1 }}>
                              Enter Email OTP<Box component="span" sx={{ color: "error.main" }}> *</Box>
                              {nbEmailVerified && (
                                <Box component="span" sx={{ color: "#10B981", fontWeight: 700, ml: 1 }}>✓ Verified</Box>
                              )}
                            </Typography>
                            <Grid container spacing={1}>
                              {nbEmailOtp.map((digit, index) => (
                                <Grid item key={index} xs={2}>
                                  <InputFieldOTP
                                    variant="outlined"
                                    type="tel"
                                    inputMode="decimal"
                                    autoComplete="one-time-code"
                                    width="100%"
                                    value={digit}
                                    disabled={nbEmailVerified}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      handleNbOtpChange(nbEmailOtp, setNbEmailOtp, nbEmailOtpRefs, index, e.target.value)
                                    }
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                      handleNbOtpKeyDown(nbEmailOtp, nbEmailOtpRefs, e, index)
                                    }
                                    inputProps={{ maxLength: 1 }}
                                    inputRef={(el: HTMLInputElement) => (nbEmailOtpRefs.current[index] = el)}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {!nbEmailVerified && (
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                                  {formatCooldown(nbEmailCooldown)}
                                </Typography>
                                <Button
                                  disabled={nbEmailCooldown > 0}
                                  onClick={handleNbResendEmail}
                                  sx={{ textTransform: "none", fontWeight: 700, color: TEAL, p: 0, minWidth: 0 }}
                                >
                                  Resend OTP
                                </Button>
                              </Box>
                            )}
                            {!nbEmailVerified && (
                              <PrimaryButton
                                fullWidth
                                height="36px"
                                fontSize="13px"
                                disabled={!isNbEmailOtpComplete || nbVerifyingEmail}
                                onClick={handleNbVerifyEmail}
                                sx={{ mt: 1.5 }}
                              >
                                <ButtonLoader title="Verify Email OTP" isLoading={nbVerifyingEmail} />
                              </PrimaryButton>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: "flex", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
                        <OutlinedPrimaryButton
                          variant="outlined"
                          height="38px"
                          borderRadius="6px"
                          onClick={handleNbChangeInfo}
                          sx={{ px: 4 }}
                        >
                          Change Info
                        </OutlinedPrimaryButton>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {verifyStep === "success" && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <PrimaryButton
                    height="38px"
                    fontSize="13px"
                    onClick={() => navigate(ROUTES.ContactUs.raiseRequest.locationPath)}
                    sx={{ px: 3 }}
                  >
                    Raise a Request
                  </PrimaryButton>
                  <OutlinedPrimaryButton
                    variant="outlined"
                    height="38px"
                    borderRadius="6px"
                    onClick={() => navigate(ROUTES.ContactUs.viewTickets.locationPath)}
                    sx={{ px: 3 }}
                  >
                    View Tickets
                  </OutlinedPrimaryButton>
                </Box>
              )}
            </Box>
          </Box>
            </Grid>

            {/* "Before you raise a request" rail — replaces the categories grid once the
                visitor is inside the verify flow (per design). */}
            {!isQuestionStage && (
              <Grid item xs={12} md={4}>
                {/* Positioning context for the rail, which is absolute on desktop so the card
                    decides the row height and the rail matches it exactly. */}
                <Box sx={{ position: "relative", height: { md: "100%" } }}>
                  {/* No onNotHelpful: on the public Help Center the answer just closes the
                      guide. The jump into raising a request is for the logged-in page and the
                      QRC "Yes, I do" journey only (see ContactUsHub). */}
                  <SupportCategoriesGrid layout="rail" railFillHeight />
                </Box>
              </Grid>
            )}
          </Grid>
          </Box>

          {/* Support categories — landing (question) stage only */}
          {isQuestionStage && (
            <Box sx={{ mt: 4 }}>
              <SupportCategoriesGrid variant="preLogin" />
            </Box>
          )}

          {/* Mobile app / portal / WhatsApp channels */}
          <DigitalChannels />
        </Box>
      </Box>

      <DialogStatusPopup />
    </Box>
  );
};

export default HelpCenter;